import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/Select';
import { DashboardBoard } from '@/components/intelligence/dashboard/DashboardBoard';
import { buildDefaultKpisLayout } from '@/features/intelligence/dashboard/defaults';
import type { ForecastSettings } from '@/features/intelligence/dashboard/evaluate';
import { KPI_RANGE_OPTIONS, isKpiRange, resolveKpiWindow, type KpiRange } from '@/features/intelligence/dashboard/range';

const DEFAULT_RANGE: KpiRange = '30d';

/** Forecast widgets dropped onto the KPI board use these unless they override them. */
const KPI_FORECAST_SETTINGS: ForecastSettings = { horizon: 6, history: 12 };

export function KpisPage() {
  const [params, setParams] = useSearchParams();
  const rangeParam = params.get('range');
  const range: KpiRange = isKpiRange(rangeParam) ? rangeParam : DEFAULT_RANGE;
  const window = useMemo(() => resolveKpiWindow(range), [range]);

  const setRange = (next: KpiRange) =>
    setParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        if (next === DEFAULT_RANGE) nextParams.delete('range');
        else nextParams.set('range', next);
        return nextParams;
      },
      { replace: true },
    );

  const controls = (
    <Select value={range} onValueChange={(value) => setRange(value as KpiRange)}>
      <SelectTrigger className="h-8 w-[180px] text-xs" aria-label="Date range">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {KPI_RANGE_OPTIONS.map((option) => (
          <SelectItem key={option.id} value={option.id}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <DashboardBoard
      page="kpis"
      title="KPIs"
      eyebrow="Intelligence"
      window={window}
      forecast={KPI_FORECAST_SETTINGS}
      buildDefault={buildDefaultKpisLayout}
      controls={controls}
      subtitle={`${window.label} · ${window.comparisonLabel}`}
    />
  );
}
